import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePublicBookingStore } from '../../../../store/publicBooking';
import { PublicBookingSuccessStep } from '../PublicBookingSuccessStep';
import { publicBookingStepPath } from '../bookingPaths';
import { useAcademySlug } from '../../../../hooks/useAcademySlug';
import { useAcademyPaths } from '../../../../hooks/useAcademyPaths';
import { useAuthStore } from '@/store/auth';
import { useGuardianPaths } from '@/hooks/useGuardianPaths';
import { resolveOrgSlugFromPublicPath } from '@/lib/resolveOrgBookingContext';

export function BookingSuccessStep() {
  const academySlug = useAcademySlug();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const guardianPaths = useGuardianPaths();
  const booking = usePublicBookingStore((s) => s.booking);
  const hasPaymentProcessing = usePublicBookingStore((s) => s.hasPaymentProcessing);
  const academy = usePublicBookingStore((s) => s.academy);
  const reset = usePublicBookingStore((s) => s.reset);
  const { p, login } = useAcademyPaths(academy);
  const isOrgBooking = resolveOrgSlugFromPublicPath() !== null;

  useEffect(() => {
    if (!booking && academySlug && lessonId) {
      navigate(publicBookingStepPath(academy ?? { slug: academySlug }, lessonId, 'register'), { replace: true });
    }
  }, [booking, academy, academySlug, lessonId, navigate]);

  const actions = useMemo(() => {
    if (user) {
      const bookingsPath = guardianPaths?.bookings ?? p('/bookings') ?? p('/dashboard');
      return bookingsPath
        ? [
            {
              label: 'View my bookings',
              onClick: () => {
                reset();
                navigate(bookingsPath);
              },
            },
          ]
        : [];
    }

    const homePath = guardianPaths?.lessons ?? p('');
    const signInPath = login(homePath || p('/dashboard') || '/');
    return [
      {
        label: isOrgBooking ? 'Back to lessons' : 'Back to academy',
        onClick: () => {
          reset();
          if (homePath) navigate(homePath);
        },
      },
      {
        label: 'Sign in to manage bookings',
        variant: 'outline' as const,
        onClick: () => navigate(signInPath),
      },
    ];
  }, [guardianPaths, isOrgBooking, login, navigate, p, reset, user]);

  if (!academySlug || !p) {
    return null;
  }

  return (
    <PublicBookingSuccessStep
      hasPaymentProcessing={hasPaymentProcessing}
      academy={academy}
      actions={actions}
    />
  );
}
